import { Link } from "react-router-dom";
import { NSWordmark, SwarmMoon, MonoChip, StatusDot } from "../kit";
import { MartzmakesWordmarkLink } from "../components/MartzmakesWordmark";
import { DemoWidgetFrame } from "../components/DemoWidgetFrame";
import {
  sessionStatusFixture,
  swarmStatusFixture,
  schedulesFixture,
  reviewFixture,
  agentsFixture,
  scheduleEditorFixture,
  queueFixture,
  tokensFixture,
} from "../fixtures/widgets";
import "../widgets.css";

/**
 * Widgets — the widget-gallery marketing page
 * (docs/widget-gallery-page.md §4). The swarm, rendered inside Claude:
 * every section embeds the REAL deployed widget document (fetched from
 * GET /mcp/ui/<name>, the bytes Claude iframes) running against one
 * coherent demo swarm — 2 machines, 3 agents, a live needs-human
 * session, a failed job, a week of token burn. Buttons work; everything
 * resolves in demo mode (fixtures/widgets/demoBridge.ts), no tenant
 * endpoints. Page language follows Landing/Docs: mkt-style topbar,
 * kickers, mono chips, the 860px breakpoint.
 */

interface GallerySection {
  id: string;
  n: string;
  /** Widget name at /mcp/ui/<name>. */
  widget: string;
  title: string;
  /** "tool → widget" pairing chip. */
  tool: string;
  desc: string;
  /** A one-line "try it" nudge for the interactive ones. */
  tryIt?: string;
}

const SECTIONS: GallerySection[] = [
  {
    id: "session-status",
    n: "01",
    widget: "session-status",
    title: "Session status",
    tool: "session_status",
    desc:
      "One running agent, one card: status, timings, the live transcript " +
      "tail. When an agent hits a permission prompt, the needs-human row " +
      "appears and you answer it without leaving the thread.",
    tryIt: "Try it — answer the prompt with 1/2/3, or hit stop.",
  },
  {
    id: "swarm-status",
    n: "02",
    widget: "swarm-status",
    title: "Swarm status",
    tool: "swarm_status",
    desc:
      "The whole swarm at a glance: machines with live dots and seat " +
      "labels, active sessions, queue counts, night-budget burn — and a " +
      "warn chip the moment a Claude seat hits its cap.",
  },
  {
    id: "schedules",
    n: "03",
    widget: "schedules",
    title: "Schedules",
    tool: "list_schedules",
    desc:
      "Every recurring run on one list — cron, agent, last fired, paused " +
      "or live. The 01:30 nightly sweep is the whole product in one row.",
  },
  {
    id: "review",
    n: "04",
    widget: "review",
    title: "Morning review",
    tool: "morning_review",
    desc:
      "Wake up to a digest, not a backlog: totals, each overnight " +
      "session's outcome with its first and last lines, and the jobs that " +
      "still need you.",
  },
  {
    id: "agents",
    n: "05",
    widget: "agents",
    title: "Agents",
    tool: "list_agents",
    desc:
      "Your roster as cards — live session dots, warm chips, and an ask " +
      "box on every card that sends a message and renders the reply " +
      "inline.",
    tryIt: "Try it — ask any agent something; the gallery answers in demo mode.",
  },
  {
    id: "schedule-editor",
    n: "06",
    widget: "schedule-editor",
    title: "Schedule editor",
    tool: "draft_schedule",
    desc:
      "Cron without the cron: pick nightly, weekdays, or an interval; " +
      "step the time; watch the six-field expression write itself. Model, " +
      "effort, and the always-run budget exemption ride along.",
    tryIt: "Try it — flip the controls and save; saving here is demo mode.",
  },
  {
    id: "queue",
    n: "07",
    widget: "queue",
    title: "Queue",
    tool: "list_jobs",
    desc:
      "Jobs grouped by state with agent, source, and age. Failed rows " +
      "retry in place; budget-held work waits as queued until the night " +
      "window allows it.",
    tryIt: "Try it — retry the failed health-sync job.",
  },
  {
    id: "tokens",
    n: "08",
    widget: "tokens",
    title: "Token usage",
    tool: "token_usage",
    desc:
      "Last week's burn as in/out bars, split by agent and by Claude seat " +
      "— rolling 5h, today, week, month. Honest lower-bound estimates, " +
      "because your caps count everything.",
  },
];

const FIXTURES: Record<string, () => Record<string, unknown>> = {
  "session-status": sessionStatusFixture,
  "swarm-status": swarmStatusFixture,
  schedules: schedulesFixture,
  review: reviewFixture,
  agents: agentsFixture,
  "schedule-editor": scheduleEditorFixture,
  queue: queueFixture,
  tokens: tokensFixture,
};

function WdgTopbar() {
  return (
    <div className="wdg-topbar">
      <div className="wdg-container wdg-topbar-inner">
        <Link to="/" className="wdg-topbar-home" aria-label="nightswarm.app home">
          <NSWordmark size={17} />
        </Link>
        <MonoChip tone="ghost">widgets</MonoChip>
        <div className="wdg-topbar-right">
          <Link to="/" className="wdg-toplink">
            Home
          </Link>
          <Link to="/docs" className="wdg-toplink">
            Docs
          </Link>
          <Link to="/dashboard" className="ns-btn ns-btn--primary ns-btn--sm">
            Open dashboard
          </Link>
        </div>
      </div>
    </div>
  );
}

function WdgHero() {
  return (
    <header className="wdg-hero">
      <div className="wdg-container">
        <div className="wdg-hero-chip">
          <MonoChip tone="ghost">
            <StatusDot tone="live" pulse size={6} /> live widget gallery
          </MonoChip>
        </div>
        <h1>
          The swarm, rendered inside Claude.
          <br />
          <span className="dim">These are live. Go ahead — click.</span>
        </h1>
        <p className="wdg-hero-sub">
          When NightSwarm answers a tool call in the Claude app, the response
          arrives as an interactive widget in the thread. Below are the eight
          shipped widgets — the exact HTML Claude renders — running against a
          seeded demo swarm: two machines, three agents, one live session
          stuck on a question. Every button resolves locally in demo mode;
          nothing touches a real swarm.
        </p>
        <div className="wdg-hero-meta">
          <MonoChip tone="ghost">2 machines</MonoChip>
          <MonoChip tone="ghost">3 agents</MonoChip>
          <MonoChip tone="accent">demo data · no sign-in needed</MonoChip>
        </div>
      </div>
    </header>
  );
}

function WidgetSection({ s, eager }: { s: GallerySection; eager: boolean }) {
  return (
    <section id={s.id} className="wdg-sec">
      <div className="wdg-container wdg-sec-grid">
        <div className="wdg-sec-copy">
          <div className="wdg-kicker">
            {s.n} · {s.widget}
          </div>
          <h2>{s.title}</h2>
          <p>{s.desc}</p>
          <div className="wdg-pairing">
            <MonoChip tone="accent">
              {s.tool} → {s.widget}
            </MonoChip>
          </div>
          {s.tryIt && <div className="wdg-tryit">{s.tryIt}</div>}
        </div>
        <div className="wdg-sec-demo">
          <div className="wdg-chrome">
            <div className="wdg-chrome-bar">
              <SwarmMoon size={14} />
              <span className="t">Claude · nightswarm</span>
              <span className="right mono">{s.tool}</span>
            </div>
            <DemoWidgetFrame
              widget={s.widget}
              pairedTool={s.tool}
              makeFixture={FIXTURES[s.widget]}
              eager={eager}
            />
          </div>
        </div>
      </div>
    </section>
  );
}

function WdgCta() {
  return (
    <section className="wdg-cta">
      <div className="wdg-container wdg-cta-inner">
        <SwarmMoon size={48} animate />
        <h2>Point Claude at your own swarm.</h2>
        <p>
          Add <code>https://nightswarm.app/mcp</code> as a custom connector
          and these widgets render your machines, your agents, your nights.
        </p>
        <div className="wdg-cta-btns">
          <Link to="/" className="ns-btn ns-btn--primary ns-btn--lg">
            Get started
          </Link>
          <Link to="/docs" className="ns-btn ns-btn--outline ns-btn--lg">
            Read the docs
          </Link>
        </div>
      </div>
    </section>
  );
}

function WdgFooter() {
  return (
    <footer className="wdg-footer">
      <div className="wdg-container wdg-footer-inner">
        <span className="wdg-footer-copy">
          © 2026 nightswarm · runs after dark
        </span>
        <nav className="wdg-footer-links" aria-label="Site">
          <Link to="/">Home</Link>
          <Link to="/docs">Docs</Link>
          <Link to="/pricing">Pricing</Link>
          <Link to="/privacy">Privacy</Link>
          <Link to="/terms">Terms</Link>
        </nav>
        <span className="wdg-footer-built">
          <span>built by</span>
          <MartzmakesWordmarkLink size={12.5} />
        </span>
      </div>
    </footer>
  );
}

export default function Widgets() {
  return (
    <>
      <WdgTopbar />
      <WdgHero />
      <main>
        {SECTIONS.map((s, i) => (
          <WidgetSection key={s.id} s={s} eager={i === 0} />
        ))}
      </main>
      <WdgCta />
      <WdgFooter />
    </>
  );
}
